import { mixed, string, number, boolean, date, array, object, addMethod, } from 'yup';
import { logicalXor } from '../math-utils';
import { enrichYupWrapper } from './yupEnrichments';
import { createSchema } from './viewerComponents/builder';
import { PANEL_TYPE_MANAGED_OPTIONS, PANEL_TYPE_SLOT } from './constants';
import * as SchemasDefaultConstraints from './leanSchemas/schemasConstraints';
import { basicTransformToDocumentSchema } from './leanSchemas/dsTransformer';
import { transformToLeanSchema, } from './index';
const withLeanSchema = (type) => ({
    leanSchema: { type },
});
export const withLeanPanel = (controlType) => ({
    leanPanel: { controlType },
});
const withAdditionalSchemas = (name, schema) => ({
    additionalSchemas: { [name]: Object.assign({}, schema) },
});
export const root = {
    mixed,
    string,
    number,
    date,
    array,
    object,
    boolean() {
        return boolean().meta(Object.assign(Object.assign({}, withLeanSchema('boolean')), withLeanPanel('boolean')));
    },
    integer() {
        return number().meta(Object.assign(Object.assign({}, withLeanSchema('integer')), withLeanPanel('integer')));
    },
    double() {
        return number().meta(Object.assign(Object.assign({}, withLeanSchema('double')), withLeanPanel('double')));
    },
    shortString() {
        return string()
            .max(SchemasDefaultConstraints.SmallStringMaxLength)
            .meta(Object.assign(Object.assign({}, withLeanSchema('small_string')), withLeanPanel('small_string')));
    },
    longString() {
        return string()
            .max(SchemasDefaultConstraints.BigStringMaxLength)
            .meta(Object.assign(Object.assign({}, withLeanSchema('big_string')), withLeanPanel('big_string')));
    },
    text() {
        return string()
            .max(SchemasDefaultConstraints.TextMaxLength)
            .meta(Object.assign(Object.assign({}, withLeanSchema('text')), withLeanPanel('text')));
    },
    stringEnum(oneOfEnum) {
        return new mixed({ type: 'string' }).oneOf(oneOfEnum).meta(Object.assign(Object.assign({}, withLeanSchema('string_enum')), withLeanPanel('dropdown')));
    },
    slot() {
        return object({
            $ref: this.stringEnum(['SlotRef']).default('SlotRef'),
        }).meta(Object.assign(Object.assign({}, withLeanSchema('slot')), withLeanPanel(PANEL_TYPE_SLOT)));
    },
    /**
     * @param {string} itemType
     * This is the array's items schema type.
     * In case no unique schema is required (i.e. propsSchemaBuilder is undefined)
     * this should be undefined as well, and the default schema will be used.
     * @param {PropsSchemaBuilderFunction} propsSchemaBuilder
     * This is the array's items actual schema builder.
     */
    itemArray(itemType, propsSchemaBuilder) {
        if (!isValidItemArraySchemaBuilder(itemType, propsSchemaBuilder)) {
            throw new Error(`expected valid itemType and propsSchemaBuilder params`);
        }
        const customItemType = itemType !== null && itemType !== void 0 ? itemType : 'defaultItemSchemaType';
        const defaultSchemaBuilder = ({ schemaBuilder, }) => ({
            label: schemaBuilder.shortString(),
            value: schemaBuilder.shortString(),
            disabled: schemaBuilder.boolean().default(false),
            isDefault: schemaBuilder.boolean().default(false),
            unavailable: schemaBuilder.boolean().default(false),
        });
        const additionalSchemas = createSchema(propsSchemaBuilder, {}, // isDraftSchema not mentioned here on purpose, will be decided later on according parent schema
        defaultSchemaBuilder);
        const additionalLeanSchema = transformToLeanSchema.fromYup(additionalSchemas.fields);
        const additionalDSSchemas = basicTransformToDocumentSchema(additionalLeanSchema);
        const withIcon = Object.values(additionalSchemas.fields).reduce((prevRes, curField) => curField.meta().leanSchema.type === 'svg' || prevRes, false);
        return array()
            .of(additionalSchemas.meta(withLeanSchema('shape')))
            .meta(Object.assign(Object.assign(Object.assign({ panelType: PANEL_TYPE_MANAGED_OPTIONS, withIcon,
            customItemType }, withAdditionalSchemas(customItemType, additionalDSSchemas)), withLeanSchema('array')), withLeanPanel('options')));
    },
    svg() {
        return object({
            svgId: this.shortString(),
        }).meta(Object.assign(Object.assign({}, withLeanSchema('svg')), withLeanPanel('media_selection')));
    },
    link() {
        return object({
            $ref: new mixed({
                type: 'document_data.json#/LinkReference',
            }).default('document_data.json#/LinkReference'),
        }).meta(Object.assign(Object.assign({}, withLeanSchema('link')), withLeanPanel('link_picker')));
    },
    colorPicker() {
        return string().meta(Object.assign(Object.assign({}, withLeanSchema('color_picker')), withLeanPanel('color_picker')));
    },
    fontPicker() {
        return string().meta(Object.assign(Object.assign({}, withLeanSchema('font_picker')), withLeanPanel('font_picker')));
    },
};
enrichYupWrapper(root);
const isValidItemArraySchemaBuilder = (itemType, propsSchemaBuilder) => !logicalXor(itemType, propsSchemaBuilder);
export const enrich = addMethod;
//# sourceMappingURL=yupWrapper.js.map